import { type ComponentType, type ReactNode, useEffect, useState } from "react";
import { trackEvent } from "@/lib/tracking";

type LazyReason = "timeout" | "missing_export" | "import_error";

/**
 * Emit a GA/analytics event AND persist an entry to the frontend error
 * log so admins can spot recurring third-party hangs. Fire-and-forget —
 * must never throw, block, or delay the UI.
 */
function reportLazyIssue(params: {
  kind: "widget_timeout" | "widget_error";
  event: "lazy_widget_timeout" | "lazy_widget_error";
  widget: string;
  reason: LazyReason;
  durationMs: number;
  message?: string;
}) {
  const { kind, event, widget, reason, durationMs, message } = params;
  try {
    trackEvent(event, {
      widget,
      reason,
      duration_ms: Math.round(durationMs),
    });
  } catch {
    /* analytics must never break UX */
  }
  if (typeof window === "undefined") return;
  try {
    const body = JSON.stringify({
      path: window.location.pathname,
      search: window.location.search || undefined,
      kind,
      error: `[LazyWidget:${widget}] ${reason} after ${Math.round(durationMs)}ms${
        message ? ` — ${message}` : ""
      }`,
      referer: document.referrer || null,
    });
    // Use keepalive so the beacon still lands if the user navigates away.
    void fetch("/api/public/frontend-error-log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* swallow */
  }
}

/**
 * Context passed to a custom `errorFallback` render function when a
 * lazy-loaded widget fails or times out.
 *
 * - `retry` re-runs the dynamic import in place. Safe to call even when
 *   the retry budget is exhausted — it will be a no-op in that case.
 * - `canRetry` is false once `maxRetries` has been reached, so custom
 *   UIs can hide or disable their "Try again" affordance.
 * - `retriesRemaining` reflects how many more retries the user has
 *   before hitting the cap (never negative).
 */
export interface LazyWidgetErrorContext {
  retry: () => void;
  canRetry: boolean;
  retriesRemaining: number;
}

/** Render prop signature for `errorFallback` — always receives {@link LazyWidgetErrorContext}. */
export type LazyWidgetErrorFallbackRenderer = (ctx: LazyWidgetErrorContext) => ReactNode;

interface LazyWidgetProps<P extends object> {
  /**
   * Dynamic import returning the module. e.g. `() => import("@/components/InstagramFeed")`
   */
  loader: () => Promise<Record<string, unknown>>;
  /** Named export to pull from the module. Defaults to "default". */
  exportName?: string;
  /**
   * Stable per-widget key used for the env-var timeout lookup and for
   * logs/analytics. If omitted, falls back to `label` then `exportName`.
   * Example: `name="instagram_feed"` reads `VITE_LAZY_TIMEOUT_INSTAGRAM_FEED`.
   */
  name?: string;
  /**
   * Hard timeout in ms. Env vars override this at build time so you can
   * tune performance without touching component code:
   *   1. `VITE_LAZY_TIMEOUT_<NAME>`  (per-widget, name uppercased + `_`)
   *   2. `VITE_LAZY_TIMEOUT_DEFAULT` (global default)
   *   3. This `timeoutMs` prop
   *   4. Built-in fallback of 4000ms
   * Widget renders an error placeholder on timeout.
   */
  timeoutMs?: number;
  /** Delay the import until the browser is idle / after N ms. Default 0 (idle). */
  deferMs?: number;
  /** Props forwarded to the loaded component. */
  componentProps?: P;
  /**
   * Custom loading placeholder. Defaults to a subtle animated skeleton so
   * the user can see the space is reserved and something is coming.
   */
  placeholder?: ReactNode;
  /**
   * Custom error/timeout placeholder. May be a ReactNode, or a render
   * function that receives the strongly-typed {@link LazyWidgetErrorContext}
   * so custom UIs can offer an in-place "Try again" without reloading.
   */
  errorFallback?: ReactNode | LazyWidgetErrorFallbackRenderer;
  /**
   * Human label used in the default skeleton/error copy and aria-label,
   * e.g. "Instagram feed" or "Latest reviews".
   */
  label?: string;
  /**
   * Approximate height for the default skeleton (CSS length). Reserves
   * space so lazy loads don't cause layout shift. Default "12rem".
   */
  minHeight?: string;
  /**
   * Opt back into silent-skip behaviour (no visible placeholder on
   * timeout/error). Useful for purely decorative widgets. Default false.
   */
  hideOnError?: boolean;
  /**
   * Maximum number of user-initiated retries after the initial load
   * (excludes attempt #0). Env vars override this so ops can tune it
   * without a redeploy:
   *   1. `VITE_LAZY_MAX_RETRIES_<NAME>`  (per-widget, name uppercased + `_`)
   *   2. `VITE_LAZY_MAX_RETRIES_DEFAULT` (global default)
   *   3. This `maxRetries` prop
   *   4. Built-in fallback of 3
   * Once exhausted, the "Try again" button is hidden/disabled and only
   * the full "Refresh page" fallback remains.
   */
  maxRetries?: number;
}

const DEFAULT_TIMEOUT_MS = 4000;
const DEFAULT_MAX_RETRIES = 3;

/** Normalize an arbitrary widget name into an env-var-safe suffix. */
export function envKeyFor(name: string): string {
  return name
    .trim()
    .replace(/[^A-Za-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toUpperCase();
}

/**
 * Read the effective hard timeout in ms. Order of precedence:
 *   1. `VITE_LAZY_TIMEOUT_<NAME>`  (per-widget env var, build-time)
 *   2. `VITE_LAZY_TIMEOUT_DEFAULT` (global env override)
 *   3. explicit `timeoutMs` prop
 *   4. `DEFAULT_TIMEOUT_MS`
 * Invalid values (non-numeric, <= 0) are ignored so a bad env var
 * cannot break the site.
 */
/** Module-scoped set so we only warn once per bad env var, not on every mount. */
export const __warnedEnvKeys = new Set<string>();

function validateEnvTimeout(envKey: string, raw: string | undefined): number | null {
  // Undefined / unset → silent (this is the normal case; not a misconfig).
  if (raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed === "") {
    if (!__warnedEnvKeys.has(envKey)) {
      __warnedEnvKeys.add(envKey);
      // eslint-disable-next-line no-console
      console.warn(
        `[LazyWidget] ${envKey} is set but empty. Falling back to the next timeout source.`,
      );
    }
    return null;
  }
  const n = Number(trimmed);
  if (!Number.isFinite(n)) {
    if (!__warnedEnvKeys.has(envKey)) {
      __warnedEnvKeys.add(envKey);
      // eslint-disable-next-line no-console
      console.warn(
        `[LazyWidget] ${envKey}="${raw}" is not a valid number. Falling back to the next timeout source.`,
      );
    }
    return null;
  }
  if (n <= 0 || !Number.isInteger(n)) {
    if (!__warnedEnvKeys.has(envKey)) {
      __warnedEnvKeys.add(envKey);
      // eslint-disable-next-line no-console
      console.warn(
        `[LazyWidget] ${envKey}=${raw} must be a positive integer in ms. Falling back to the next timeout source.`,
      );
    }
    return null;
  }
  return n;
}

export function resolveTimeoutMs(name: string, propTimeout: number | undefined): number {
  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const perWidgetKey = `VITE_LAZY_TIMEOUT_${envKeyFor(name)}`;
  const perWidget = validateEnvTimeout(perWidgetKey, env[perWidgetKey]);
  const globalDefault = validateEnvTimeout("VITE_LAZY_TIMEOUT_DEFAULT", env.VITE_LAZY_TIMEOUT_DEFAULT);
  return (
    perWidget ??
    globalDefault ??
    (typeof propTimeout === "number" && propTimeout > 0 ? propTimeout : DEFAULT_TIMEOUT_MS)
  );
}

/** Validate a `VITE_LAZY_MAX_RETRIES_*` env value. Accepts 0 (disables retry). */
function validateEnvMaxRetries(envKey: string, raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
    if (!__warnedEnvKeys.has(envKey)) {
      __warnedEnvKeys.add(envKey);
      // eslint-disable-next-line no-console
      console.warn(
        `[LazyWidget] ${envKey}="${raw}" must be a non-negative integer. Falling back to the next source.`,
      );
    }
    return null;
  }
  return n;
}

/**
 * Read the effective retry cap. Precedence mirrors `resolveTimeoutMs`:
 * per-widget env → global env → prop → built-in default.
 */
export function resolveMaxRetries(name: string, propMax: number | undefined): number {
  const env = (import.meta.env ?? {}) as Record<string, string | undefined>;
  const perWidgetKey = `VITE_LAZY_MAX_RETRIES_${envKeyFor(name)}`;
  const perWidget = validateEnvMaxRetries(perWidgetKey, env[perWidgetKey]);
  const globalDefault = validateEnvMaxRetries(
    "VITE_LAZY_MAX_RETRIES_DEFAULT",
    env.VITE_LAZY_MAX_RETRIES_DEFAULT,
  );
  return (
    perWidget ??
    globalDefault ??
    (typeof propMax === "number" && Number.isInteger(propMax) && propMax >= 0
      ? propMax
      : DEFAULT_MAX_RETRIES)
  );
}


/**
 * Lazy-load a non-essential widget with a hard timeout and visible states.
 *
 * - Import happens after mount, in `requestIdleCallback` (or setTimeout fallback),
 *   so it never blocks the main render path.
 * - While loading, a lightweight skeleton reserves the space so users know
 *   content is on the way (and layout doesn't shift when it arrives).
 * - If the import doesn't resolve within the effective timeout or throws, a
 *   small error placeholder is shown instead of silently skipping — unless
 *   `hideOnError` is true.
 * - The timeout is env-var configurable per widget; see `timeoutMs` docs.
 */
export function LazyWidget<P extends object = Record<string, never>>({
  loader,
  exportName = "default",
  name,
  timeoutMs,
  deferMs = 0,
  componentProps,
  placeholder,
  errorFallback,
  label,
  minHeight = "12rem",
  hideOnError = false,
  maxRetries,
}: LazyWidgetProps<P>) {
  const [Component, setComponent] = useState<ComponentType<P> | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [attempt, setAttempt] = useState(0);
  const widgetName = name || label || exportName;
  const effectiveTimeoutMs = resolveTimeoutMs(widgetName, timeoutMs);
  const effectiveMaxRetries = resolveMaxRetries(widgetName, maxRetries);
  // `attempt` counts total load attempts including the initial mount (0-indexed).
  // Retries used so far = attempt (since attempt increments once per retry click).
  const retriesRemaining = Math.max(0, effectiveMaxRetries - attempt);
  const canRetry = retriesRemaining > 0;

  const retry = () => {
    if (!canRetry) {
      // Budget exhausted — swallow the click so custom fallbacks that
      // forget to check `canRetry` still can't spin forever.
      // eslint-disable-next-line no-console
      console.warn(
        `[LazyWidget] '${widgetName}' retry ignored — max ${effectiveMaxRetries} retries reached.`,
      );
      return;
    }
    // Fire-and-forget analytics: user asked to recover the widget in place.
    try {
      trackEvent("lazy_widget_retry", {
        widget: widgetName,
        attempt: attempt + 1,
        max_retries: effectiveMaxRetries,
      });
    } catch {
      /* analytics must never break UX */
    }
    setComponent(null);
    setStatus("loading");
    setAttempt((n) => n + 1);
  };


  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    const isRetry = attempt > 0;

    const reportRetryOutcome = (outcome: "success" | "timeout" | "error", reason?: string) => {
      if (!isRetry) return;
      try {
        trackEvent("lazy_widget_retry_result", {
          widget: widgetName,
          attempt,
          outcome,
          reason: reason ?? "",
          duration_ms: Math.round(Date.now() - startedAt),
        });
      } catch {
        /* analytics must never break UX */
      }
    };

    const timeout = setTimeout(() => {
      if (!cancelled && !Component) {
        setStatus("error");
        const duration = Date.now() - startedAt;
        // eslint-disable-next-line no-console
        console.warn(`[LazyWidget] '${widgetName}' timed out after ${effectiveTimeoutMs}ms.`);
        reportLazyIssue({
          kind: "widget_timeout",
          event: "lazy_widget_timeout",
          widget: widgetName,
          reason: "timeout",
          durationMs: duration,
        });
        reportRetryOutcome("timeout", "timeout");
      }
    }, effectiveTimeoutMs);

    const runImport = () => {
      loader()
        .then((mod) => {
          if (cancelled) return;
          const resolved = mod[exportName] as ComponentType<P> | undefined;
          if (typeof resolved === "function") {
            clearTimeout(timeout);
            setComponent(() => resolved);
            setStatus("ready");
            reportRetryOutcome("success");
          } else {
            clearTimeout(timeout);
            setStatus("error");
            const duration = Date.now() - startedAt;
            // eslint-disable-next-line no-console
            console.warn(`[LazyWidget] export '${exportName}' missing in module.`);
            reportLazyIssue({
              kind: "widget_error",
              event: "lazy_widget_error",
              widget: widgetName,
              reason: "missing_export",
              durationMs: duration,
              message: `export '${exportName}' not found`,
            });
            reportRetryOutcome("error", "missing_export");
          }
        })
        .catch((err) => {
          if (cancelled) return;
          clearTimeout(timeout);
          setStatus("error");
          const duration = Date.now() - startedAt;
          // eslint-disable-next-line no-console
          console.warn(`[LazyWidget] '${widgetName}' failed to load:`, err);
          reportLazyIssue({
            kind: "widget_error",
            event: "lazy_widget_error",
            widget: widgetName,
            reason: "import_error",
            durationMs: duration,
            message: err instanceof Error ? err.message : String(err),
          });
          reportRetryOutcome("error", "import_error");
        });
    };

    const schedule = () => {
      if (typeof window !== "undefined" && "requestIdleCallback" in window) {
        (window as Window & {
          requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number;
        }).requestIdleCallback(runImport, { timeout: Math.max(effectiveTimeoutMs - 100, 250) });
      } else {
        setTimeout(runImport, 0);
      }
    };

    const deferTimer = deferMs > 0 ? setTimeout(schedule, deferMs) : (schedule(), null);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      if (deferTimer) clearTimeout(deferTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  if (status === "ready" && Component) {
    return <Component {...(componentProps ?? ({} as P))} />;
  }

  if (status === "error") {
    if (hideOnError) return null;
    if (typeof errorFallback === "function")
      return <>{errorFallback({ retry, canRetry, retriesRemaining })}</>;
    if (errorFallback !== undefined) return <>{errorFallback}</>;
    return (
      <DefaultError
        label={label}
        minHeight={minHeight}
        onRetry={canRetry ? retry : undefined}
        retriesRemaining={retriesRemaining}
      />
    );
  }


  // loading
  if (placeholder !== undefined) return <>{placeholder}</>;
  return <DefaultSkeleton label={label} minHeight={minHeight} />;
}

function DefaultSkeleton({ label, minHeight }: { label?: string; minHeight: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label ? `Loading ${label}` : "Loading content"}
      className="w-full animate-pulse rounded-lg border border-border/40 bg-muted/40 p-4"
      style={{ minHeight }}
    >
      <div className="mb-3 h-4 w-1/3 rounded bg-muted-foreground/20" />
      <div className="mb-2 h-3 w-full rounded bg-muted-foreground/15" />
      <div className="mb-2 h-3 w-11/12 rounded bg-muted-foreground/15" />
      <div className="h-3 w-2/3 rounded bg-muted-foreground/15" />
      <span className="sr-only">{label ? `Loading ${label}…` : "Loading…"}</span>
    </div>
  );
}

function DefaultError({
  label,
  minHeight,
  onRetry,
  retriesRemaining,
}: {
  label?: string;
  minHeight: string;
  onRetry?: () => void;
  retriesRemaining?: number;
}) {
  const exhausted = onRetry === undefined && (retriesRemaining ?? 1) === 0;
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border/60 bg-muted/20 p-4 text-center text-sm text-muted-foreground"
      style={{ minHeight }}
    >
      <p className="m-0">
        {label ? `${label} couldn't load right now.` : "This section couldn't load right now."}
      </p>
      {exhausted ? (
        <p className="m-0 text-xs opacity-80">
          We've tried a few times without luck. Please refresh the page.
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            aria-label={label ? `Try loading ${label} again` : "Try loading this section again"}
            className="rounded-md border border-border/60 bg-background px-3 py-1 text-xs font-medium text-foreground shadow-sm hover:bg-muted focus:outline-none focus:ring-2 focus:ring-current"
          >
            Try again
          </button>
        ) : null}
        <button
          type="button"
          onClick={() => {
            if (typeof window !== "undefined") window.location.reload();
          }}
          className="text-xs underline underline-offset-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-current"
        >
          Refresh page
        </button>
      </div>
    </div>
  );
}
